import api from './api';

// Load Razorpay script
const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

export const razorpayService = {
  /**
   * Create registration payment order
   */
  createRegistrationOrder: async (userData) => {
    try {
      const response = await api.post('/users/payment/registration/create-order/', {
        email: userData.email,
        phone: userData.phone,
        name: userData.name || userData.username
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Create project submission payment order
   */
  createProjectOrder: async (projectId) => {
    try {
      const response = await api.post('/users/payment/project/create-order/', {
        project_id: projectId
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Verify payment after successful transaction
   */
  verifyPayment: async (paymentData) => {
    try {
      const response = await api.post('/users/payment/verify/', paymentData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Record payment failure
   */
  recordPaymentFailure: async (orderId, errorDescription) => {
    try {
      const response = await api.post('/users/payment/failed/', {
        razorpay_order_id: orderId,
        error_description: errorDescription
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Open Razorpay payment modal for registration
   */
  openRegistrationPayment: async (userData, onSuccess, onFailure) => {
    const scriptLoaded = await loadRazorpayScript();
    
    if (!scriptLoaded) {
      alert('Failed to load Razorpay SDK. Please check your internet connection.');
      return;
    }

    try {
      const orderData = await razorpayService.createRegistrationOrder(userData);
      
      const options = {
        key: orderData.key_id,
        amount: orderData.amount,
        currency: orderData.currency,
        name: 'TICSPL',
        description: 'Registration Fee',
        order_id: orderData.order_id,
        prefill: {
          name: userData.name || userData.username,
          email: userData.email,
          contact: userData.phone
        },
        theme: {
          color: '#1976d2'
        },
        handler: async function (response) {
          try {
            // Verify payment
            const verificationResult = await razorpayService.verifyPayment({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature
            });
            
            onSuccess(verificationResult, response);
          } catch (error) {
            onFailure(error);
          }
        },
        modal: {
          ondismiss: function() {
            razorpayService.recordPaymentFailure(orderData.order_id, 'Payment cancelled by user');
            onFailure(new Error('Payment cancelled'));
          }
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.on('payment.failed', async function (response) {
        await razorpayService.recordPaymentFailure(
          orderData.order_id,
          response.error.description
        );
        onFailure(response.error);
      });
      
      razorpay.open();
    } catch (error) {
      onFailure(error);
    }
  },

  /**
   * Open Razorpay payment modal for project submission
   */
  openProjectPayment: async (projectId, onSuccess, onFailure) => {
    const scriptLoaded = await loadRazorpayScript();
    
    if (!scriptLoaded) {
      alert('Failed to load Razorpay SDK. Please check your internet connection.');
      return;
    }

    try {
      const orderData = await razorpayService.createProjectOrder(projectId);
      
      const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
      
      const options = {
        key: orderData.key_id,
        amount: orderData.amount,
        currency: orderData.currency,
        name: 'TICSPL',
        description: 'Project Submission Fee',
        order_id: orderData.order_id,
        prefill: {
          name: userInfo.username || '',
          email: userInfo.email || '',
          contact: userInfo.phone || ''
        },
        theme: {
          color: '#1976d2'
        },
        handler: async function (response) {
          try {
            // Verify payment
            const verificationResult = await razorpayService.verifyPayment({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature
            });
            
            onSuccess(verificationResult, response);
          } catch (error) {
            onFailure(error);
          }
        },
        modal: {
          ondismiss: function() {
            razorpayService.recordPaymentFailure(orderData.order_id, 'Payment cancelled by user');
            onFailure(new Error('Payment cancelled'));
          }
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.on('payment.failed', async function (response) {
        await razorpayService.recordPaymentFailure(
          orderData.order_id,
          response.error.description
        );
        onFailure(response.error);
      });
      
      razorpay.open();
    } catch (error) {
      onFailure(error);
    }
  }
};

export default razorpayService;
